import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  Team,
  Player,
  Match,
  SetRecord,
  Rally,
  MatchState,
  LostReason,
  TossChoice,
  MatchType,
  TeamState,
  RotationState,
  Tournament,
  getMatchRules,
  MatchRulesConfig,
  TournamentRules,
  DEFAULT_TOURNAMENT_RULES,
  ActiveTimeout,
} from '@/types';
import { getTeams, createMatch, updateMatchStatus, createSet, updateSet, createRally, deleteRally, getMatchWithDetails, getRalliesBySet, updateMatchBestCatcher, discardMatch as discardMatchInDB, getTournament, startScheduledMatch, updateMatchTimes, updateMatchServingTeam, startTimeout, endTimeout, getActiveTimeout, getTimeoutCounts } from '@/lib/supabase';
import {
  checkSetWin,
  checkMatchWin,
  getFirstServingTeam,
  getEligibleServers,
  validateRotationGap,
  validateFirstServerTeam,
  checkRotationDeadlock,
  validatePointWinnerRequired,
} from '@/lib/validation';
import { saveMatchRef, loadMatchRef, clearMatchRef, PersistedMatchRef } from '@/lib/persistence';
import { useAuth } from '@/lib/auth';
import { capture, capturePageView } from '@/lib/posthog';

/** Determine which team started serving in a set by looking at the first rally. */
function getSetStarterTeamId(rallies: Rally[] | undefined): string | null {
  if (!rallies || rallies.length === 0) return null;
  const firstRally = rallies.reduce((min, r) => (r.rallyNumber < min.rallyNumber ? r : min));
  return firstRally.servingTeamId;
}

/**
 * Single source of truth for determining the starting team of a new set.
 * Alternates from the previous set's starter (NOT who won/lost).
 * Set N starter = opposite of Set N-1 starter.
 */
function getNextSetStarter(
  previousSetRallies: Rally[] | undefined,
  previousSetWinnerId: string | null,
  teamAId: string,
  teamBId: string,
  tossWonById: string,
  tossChoice: TossChoice,
): string {
  const previousStarter = getSetStarterTeamId(previousSetRallies);
  if (previousStarter) {
    return previousStarter === teamAId ? teamBId : teamAId;
  }
  // Fallback: loser serves (preserves old behavior when rally history is unavailable)
  if (previousSetWinnerId) {
    return previousSetWinnerId === teamAId ? teamBId : teamAId;
  }
  // Final fallback: derive from toss
  return getFirstServingTeam(tossWonById, tossChoice, teamAId, teamBId);
}
import Login from '@/components/screens/Login';
import ProtectedRoute from '@/components/ProtectedRoute';
import MatchSetup from '@/components/screens/MatchSetup';
import FirstServerSelection from '@/components/screens/FirstServerSelection';
import LiveScoring from '@/components/screens/LiveScoring';
import MatchComplete from '@/components/screens/MatchComplete';
import AdminDashboard from '@/components/screens/AdminDashboard';
import AdminPanel from '@/components/screens/AdminPanel';
import UserManagement from '@/components/screens/UserManagement';
import ClubSettings from '@/components/screens/ClubSettings';
import TournamentManagement from '@/components/screens/TournamentManagement';
import TournamentDashboard from '@/components/screens/TournamentDashboard';
import ScorerDashboard from '@/components/screens/ScorerDashboard';
import AdminMatchManagement from '@/components/screens/AdminMatchManagement';
import RallyEditor from '@/components/screens/RallyEditor';
import BroadcastControl from '@/components/screens/BroadcastControl';
import MatchView from '@/components/screens/MatchView';
import AppHeader from '@/components/AppHeader';
import DevBanner from '@/components/DevBanner';
import PublicLiveScoreboard from '@/components/screens/PublicLiveScoreboard';
import PublicTournamentCentre from '@/components/screens/PublicTournamentCentre';
import PublicPlayerProfile from '@/components/screens/PublicPlayerProfile';
import BroadcastOverlay from '@/components/screens/BroadcastOverlay';

interface ResumeMatchScreenProps {
  matchState: MatchState;
  servingTeam: TeamState | null;
  currentServer: Player | null;
  eligibleServers: any;
  teams: Team[];
  recoveryLoading: boolean;
  recoverMatch: (matchId?: string) => Promise<void>;
  resetMatch: () => void;
  updateBestCatcher: (playerIds: string[]) => Promise<void>;
  recordRally: (winningTeamId: string, lostReason: LostReason | null, pointWinnerId: string | null) => Promise<void>;
  selectNextServer: (player: Player) => Promise<void>;
  undoLastRally: () => Promise<void>;
  undoServerSelection: () => Promise<void>;
  canUndoServerSelection: () => { canUndo: boolean; servingTeamId: string | null };
  startNewSet: (firstServingTeamId: string, firstServerId: string) => Promise<void>;
  recordServeFoul: (server: Player) => Promise<void>;
  fetchSetRallies: (setId: string) => Promise<Rally[]>;
  selectFirstServer: (firstServingTeamId: string, player: Player) => Promise<void>;
  signOut: () => void;
  onExitToTournament: (tournamentId: string) => void;
  activeTimeout?: ActiveTimeout | null;
  timeoutCounts?: Map<string, number>;
  onStartTimeout?: (teamId: string, teamName: string) => Promise<void>;
  onEndTimeout?: () => Promise<void>;
}

type ResumePhase = 'loading-info' | 'recovery-ui' | 'resuming' | 'scoring' | 'error';

function ResumeMatchScreen({
  matchState, servingTeam, currentServer, eligibleServers, teams,
  recoveryLoading, recoverMatch, resetMatch, updateBestCatcher, recordRally,
  selectNextServer, undoLastRally, undoServerSelection, canUndoServerSelection,
  startNewSet, recordServeFoul, fetchSetRallies, selectFirstServer, signOut,
  onExitToTournament,
  activeTimeout,
  timeoutCounts,
  onStartTimeout,
  onEndTimeout,
}: ResumeMatchScreenProps) {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { isAdmin, profile } = useAuth();
  const isViewer = profile?.role === 'viewer';
  const [phase, setPhase] = useState<ResumePhase>('loading-info');
  const [matchInfo, setMatchInfo] = useState<{ matchNumber: number; status: string; teamAId: string; teamBId: string } | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  // Redirect viewers to read-only view
  useEffect(() => {
    if (isViewer && matchId) {
      navigate(`/match/${matchId}/view`, { replace: true });
    }
  }, [isViewer, matchId]);

  // Load minimal match info for the recovery UI display
  useEffect(() => {
    if (!matchId) { setPhase('error'); return; }
    console.log('[ResumeMatch] Clicked Match ID:', matchId);
    getMatchWithDetails(matchId)
      .then(data => {
        if (data.status === 'discarded' || data.status === 'scheduled') {
          clearMatchRef();
          setPhase('error');
          return;
        }
        setMatchInfo({
          matchNumber: data.match_number,
          status: data.status,
          teamAId: data.team_a_id,
          teamBId: data.team_b_id,
          ...(data.tournament_id ? { tournamentId: data.tournament_id } : {}),
        } as any);
        setPhase('recovery-ui');
      })
      .catch(() => setPhase('error'));
  }, [matchId]);

  const handleResume = async () => {
    if (!matchId) return;
    console.log('[handleResume] matchId:', matchId, '| teams loaded:', teams.length);
    setPhase('resuming');
    try {
      await recoverMatch(matchId);
      setPhase('scoring');
    } catch (err) {
      console.error('[handleResume] recoverMatch failed:', err);
      setPhase('error');
    }
  };

  const handleDiscard = async () => {
    if (!matchId) return;
    setDiscarding(true);
    console.log('[handleDiscard] Starting discard for match:', matchId);
    try {
      await discardMatchInDB(matchId);
      console.log('[handleDiscard] discardMatch resolved, clearing local state');
      clearMatchRef();
      resetMatch();
      const tid = (matchInfo as any)?.tournamentId;
      if (tid) navigate(`/tournament/${tid}`, { replace: true });
      else navigate('/tournaments', { replace: true });
    } catch (err) {
      console.error('[handleDiscard] Failed:', err);
      setDiscarding(false);
      setShowDiscardConfirm(false);
    }
  };

  const handleBackToTournament = () => {
    const tid = (matchInfo as any)?.tournamentId;
    if (tid) navigate(`/tournament/${tid}`);
    else navigate(isAdmin ? '/admin' : '/scorer');
  };

  const teamA = teams.find(t => t.id === matchInfo?.teamAId);
  const teamB = teams.find(t => t.id === matchInfo?.teamBId);

  // Once scoring is active (recoverMatch resolved and matchState loaded), show the scoring screens.
  // Only trust phase === 'scoring' — the 'resuming' sub-condition is unsafe because matchState
  // may still hold the previous match's data when recoveryLoading flips to false.
  const isScoring = phase === 'scoring';

  if (phase === 'loading-info') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (phase === 'error' || (!matchInfo && phase !== 'resuming' && phase !== 'scoring')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-text-secondary mb-2">Could not load match.</p>
          <p className="text-text-muted text-sm mb-4">The match may have been discarded, or you may not have access.</p>
          <button
            onClick={() => navigate(isAdmin ? '/admin' : '/scorer')}
            className="btn-secondary px-6 py-3 rounded-lg bg-surface border border-border text-text-primary font-semibold"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'resuming' && (recoveryLoading || matchState.status === 'setup')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isScoring) {
    if (matchState.status === 'first-server') {
      return (
        <FirstServerSelection
          matchState={matchState}
          teams={teams}
          onSelectServer={selectFirstServer}
          onSignOut={signOut}
          readOnly={isViewer}
        />
      );
    }
    if (matchState.status === 'match-won') {
      return (
        <MatchComplete
          matchState={matchState}
          teams={teams}
          onNewMatch={resetMatch}
          onUpdateBestCatcher={updateBestCatcher}
          onSignOut={signOut}
          readOnly={isViewer}
        />
      );
    }
    if (matchState.status !== 'setup') {
      const tid = (matchInfo as any)?.tournamentId as string | undefined;
      return (
        <LiveScoring
          matchState={matchState}
          servingTeam={servingTeam}
          currentServer={currentServer}
          eligibleServers={eligibleServers}
          serverSelectionNeeded={!!matchState.currentSet && !matchState.currentSet.currentServerId}
          onRecordRally={recordRally}
          onSelectNextServer={selectNextServer}
          onUndo={undoLastRally}
          onUndoServerSelection={undoServerSelection}
          canUndoServerSelection={canUndoServerSelection}
          onStartNewSet={startNewSet}
          onRecordServeFoul={recordServeFoul}
          onFetchSetRallies={fetchSetRallies}
          onUpdateBestCatcher={updateBestCatcher}
          onSignOut={signOut}
          tournamentId={tid}
          onExitToTournament={tid ? onExitToTournament : undefined}
          readOnly={isViewer}
          activeTimeout={activeTimeout}
          timeoutCounts={timeoutCounts}
          onStartTimeout={onStartTimeout}
          onEndTimeout={onEndTimeout}
        />
      );
    }
  }

  // Recovery UI
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={signOut}
        tournamentId={(matchInfo as any)?.tournamentId ?? undefined}
        navIcon={isAdmin ? 'admin' : 'home'}
        onNavigate={handleBackToTournament}
      />

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Active Match Card */}
          <div className="bg-warning/10 border border-warning/30 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-3 h-3 rounded-full bg-warning animate-pulse" />
              <h2 className="text-sm font-bold text-warning uppercase tracking-wider">Active Match Found</h2>
            </div>

            {teamA && teamB ? (
              <div className="bg-surface rounded-lg border border-border p-4 mb-1">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-text-muted uppercase">Match</span>
                  <span className="text-sm font-bold text-text-primary">#{matchInfo?.matchNumber}</span>
                </div>
                <div className="flex items-center justify-center gap-4 py-2">
                  <div className="text-center">
                    <div className="w-4 h-4 rounded-full mx-auto mb-1" style={{ backgroundColor: teamA.color }} />
                    <span className="text-sm font-bold" style={{ color: teamA.color }}>{teamA.shortName}</span>
                  </div>
                  <span className="text-text-muted text-sm font-bold">VS</span>
                  <div className="text-center">
                    <div className="w-4 h-4 rounded-full mx-auto mb-1" style={{ backgroundColor: teamB.color }} />
                    <span className="text-sm font-bold" style={{ color: teamB.color }}>{teamB.shortName}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-text-muted uppercase">Status</span>
                  <span className="text-xs font-semibold text-warning uppercase">{matchInfo?.status.replace('-', ' ')}</span>
                </div>
              </div>
            ) : (
              <div className="bg-surface rounded-lg border border-border p-4 mb-1">
                <p className="text-sm text-text-muted text-center">Match #{matchInfo?.matchNumber}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleResume}
              disabled={teams.length === 0}
              className="w-full py-4 px-6 rounded-xl font-bold text-base bg-success text-white transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-60"
            >
              {teams.length === 0 ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  LOADING...
                </span>
              ) : isViewer ? 'VIEW MATCH' : 'RESUME MATCH'}
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowDiscardConfirm(true)}
                className="w-full py-4 px-6 rounded-xl font-bold text-base bg-surface border-2 border-error text-error transition-all active:scale-[0.98] hover:bg-error/5"
              >
                DISCARD MATCH
              </button>
            )}
            <button
              onClick={handleBackToTournament}
              className="w-full py-3 px-6 rounded-xl font-semibold text-sm bg-surface border border-border text-text-secondary transition-all active:scale-[0.98] hover:bg-surface-raised"
            >
              {(matchInfo as any)?.tournamentId ? 'BACK TO TOURNAMENT' : isAdmin ? 'BACK TO ADMIN' : 'BACK TO DASHBOARD'}
            </button>
          </div>
        </div>
      </div>

      {/* Discard Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-text-primary mb-2">Discard Match?</h3>
            <p className="text-sm text-text-secondary mb-6">
              This will permanently delete Match #{matchInfo?.matchNumber} and all its rally data. This cannot be undone.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowDiscardConfirm(false)}
                disabled={discarding}
                className="py-3 px-4 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border transition-all active:scale-[0.98] disabled:opacity-50"
              >
                CANCEL
              </button>
              <button
                onClick={handleDiscard}
                disabled={discarding}
                className="py-3 px-4 rounded-lg font-bold text-sm bg-error text-white transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {discarding ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    DELETING...
                  </span>
                ) : 'DISCARD'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  // useAuth must be called at the top level, before any early returns (Rules of Hooks)
  const { user, profile, signOut } = useAuth();
  const isViewer = profile?.role === 'viewer';

  // Page view tracking on every route change
  useEffect(() => {
    capturePageView();
  }, [location.pathname]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [matchState, setMatchState] = useState<MatchState>({
    match: null,
    teamA: null,
    teamB: null,
    currentSetNumber: 0,
    currentSet: null,
    status: 'setup',
    sets: [],
    bestCatcherIds: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recoveryRef, setRecoveryRef] = useState<PersistedMatchRef | null>(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const autoSaveEnabled = useRef(false);
  const initialLoadDone = useRef(false);
  const serverSelectionSnapshots = useRef<Map<string, { rallyCount: number; previousRotationState: RotationState }>>(new Map());
  // Timeout state
  const [activeTimeout, setActiveTimeout] = useState<ActiveTimeout | null>(null);
  const [timeoutCounts, setTimeoutCounts] = useState<Map<string, number>>(new Map());
  const selectingFirstServer = useRef(false);
  const undoInProgress = useRef(false);
  const rallyInProgress = useRef(false);

  // Tournament context from navigation state
  const locationState = (location.state as { tournamentId?: string; tournament?: Tournament; startScheduledMatchId?: string; scheduledMatch?: any } | null);
  const preselectedTournament = locationState?.tournament || null;
  const preselectedTournamentId = locationState?.tournamentId || null;
  const startScheduledMatchId = locationState?.startScheduledMatchId || null;
  const scheduledMatchData = locationState?.scheduledMatch || null;

  // Get match rules config from current match state
  const getMatchRulesConfig = useCallback((): MatchRulesConfig | null => {
    if (!matchState.match) return null;
    return getMatchRules(
      matchState.match.matchType,
      matchState.tournamentRules || null,
      matchState.match.winningPoint,
      matchState.match.goldenPoint
    );
  }, [matchState.match, matchState.tournamentRules]);

  // Load teams + check for recoverable match on mount
  useEffect(() => {
    loadTeams();
  }, []);

  // Auto-save: persist match ref whenever matchState changes (after initial load)
  useEffect(() => {
    if (!autoSaveEnabled.current) return;
    const { match, status } = matchState;
    if (match && match.teamAId && match.teamBId && status !== 'setup') {
      saveMatchRef({
        matchId: match.id,
        teamAId: match.teamAId,
        teamBId: match.teamBId,
        status,
        tournamentId: match.tournamentId,
        activeTimeoutId: activeTimeout?.id ?? null,
      });
    }
    if (status === 'setup' && !match) {
      clearMatchRef();
    }
  }, [matchState]);

  const formatTeams = (data: Array<{ id: string; name: string; short_name: string; color: string; logo_url?: string | null; players: Array<{ id: string; name: string; number: number; team_id: string }> }>): Team[] =>
    data.map(t => ({
      id: t.id,
      name: t.name,
      shortName: t.short_name,
      color: t.color,
      logoUrl: t.logo_url,
      players: t.players.map(p => ({
        id: p.id,
        name: p.name,
        number: p.number,
        teamId: p.team_id,
      })),
    }));

  const loadTeams = async () => {
    try {
      setLoading(true);
      const data = await getTeams();
      setTeams(formatTeams(data));

      // Validate scheduled match from location state — clear if it no longer exists in DB
      if (startScheduledMatchId) {
        try {
          await getMatchWithDetails(startScheduledMatchId);
        } catch {
          // Match doesn't exist (wrong environment, deleted, etc.) — clear stale nav state
          navigate(location.pathname, { replace: true, state: {} });
        }
      }

      // Check for recoverable match — verify it's still live in DB
      const ref = loadMatchRef();
      if (ref && ref.status !== 'match-won') {
        try {
          const matchData = await getMatchWithDetails(ref.matchId);
          if (matchData.status === 'discarded' || matchData.status === 'match-won' || matchData.status === 'scheduled') {
            clearMatchRef();
          } else {
            setRecoveryRef(ref);
          }
        } catch {
          // Match no longer exists; clear stale ref
          clearMatchRef();
        }
      }
    } catch (err) {
      setError('Failed to load teams');
      console.error(err);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
      autoSaveEnabled.current = true;
    }
  };

  const reloadTeams = async () => {
    try {
      const data = await getTeams();
      setTeams(formatTeams(data));
    } catch (err) {
      console.error('Failed to reload teams:', err);
    }
  };

  const fetchSetRallies = useCallback(async (setId: string): Promise<Rally[]> => {
    const data = await getRalliesBySet(setId);
    return data.map((r: {
      id: string; set_id: string; rally_number: number;
      serving_team_id: string; server_id: string;
      point_won_by_id: string; lost_reason: string | null;
      point_winner_id: string | null; recorded_at: string;
    }) => ({
      id: r.id,
      setId: r.set_id,
      rallyNumber: r.rally_number,
      servingTeamId: r.serving_team_id,
      serverId: r.server_id,
      pointWonById: r.point_won_by_id,
      lostReason: r.lost_reason as LostReason,
      pointWinnerId: r.point_winner_id,
      recordedAt: r.recorded_at,
    }));
  }, []);

  const recoverMatch = useCallback(async (overrideMatchId?: string) => {
    const targetId = overrideMatchId ?? recoveryRef?.matchId;
    if (!targetId) throw new Error('No match ID');
    if (teams.length === 0) throw new Error('Teams not loaded yet');
    console.log('[recoverMatch] matchId:', targetId, '| teams:', teams.length);
    if (!recoveryRef || recoveryRef.matchId !== targetId) {
      setRecoveryRef({ matchId: targetId, teamAId: '', teamBId: '', status: 'live' });
    }
    setRecoveryLoading(true);
    try {
      const data = await getMatchWithDetails(targetId);
      console.log('[recoverMatch] match status:', data?.status, '| sets:', data?.sets?.length);

      if (data.status === 'discarded') {
        clearMatchRef();
        setRecoveryRef(null);
        setRecoveryLoading(false);
        return;
      }

      const teamA = teams.find(t => t.id === data.team_a_id);
      const teamB = teams.find(t => t.id === data.team_b_id);
      if (!teamA || !teamB) {
        clearMatchRef();
        setRecoveryRef(null);
        return;
      }

      const formattedMatch: Match = {
        id: data.id,
        matchNumber: data.match_number,
        date: data.date,
        matchType: data.match_type as MatchType,
        teamAId: data.team_a_id,
        teamBId: data.team_b_id,
        tossWonById: data.toss_won_by_id,
        tossChoice: data.toss_choice as TossChoice,
        status: data.status as MatchState['status'],
        winnerId: data.winner_id,
        bestCatcherIds: data.best_catcher_ids || [],
        tournamentId: data.tournament_id,
        matchTime: data.match_time,
        winningPoint: data.winning_point,
        goldenPoint: data.golden_point,
        isLocked: data.is_locked ?? false,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      // Log match rules for debugging
      console.log('[recoverMatch] Match rules:', {
        matchId: data.id,
        matchType: data.match_type,
        winningPoint: data.winning_point,
        goldenPoint: data.golden_point,
        tournamentId: data.tournament_id,
      });

      // Fetch tournament rules if needed
      let tournamentRules: TournamentRules | null = null;
      if (data.tournament_id) {
        try {
          const tournament = await getTournament(data.tournament_id);
          tournamentRules = {
            leagueWinningPoint: tournament.leagueWinningPoint,
            leagueGoldenPoint: tournament.leagueGoldenPoint,
            playoffWinningPoint: tournament.playoffWinningPoint,
            playoffGoldenPoint: tournament.playoffGoldenPoint,
          };
          console.log('[recoverMatch] Tournament rules loaded:', tournamentRules);
        } catch (err) {
          console.warn('[recoverMatch] Could not fetch tournament rules');
        }
      }

      // Helper to check golden point status using match rules
      const isGoldenPointState = (scoreA: number, scoreB: number): boolean => {
        const goldenPoint = data.golden_point ?? (data.match_type === 'league' ? DEFAULT_TOURNAMENT_RULES.leagueGoldenPoint : DEFAULT_TOURNAMENT_RULES.playoffGoldenPoint);
        const goldenPointTrigger = goldenPoint - 1;
        return scoreA === goldenPointTrigger && scoreB === goldenPointTrigger;
      };

      // Build sets and rallies
      const sets: SetRecord[] = [];
      let currentSetNumber = 0;
      let currentSet: MatchState['currentSet'] = null;

      for (const s of data.sets) {
        const rallies: Rally[] = s.rallies.map((r: {
          id: string; set_id: string; rally_number: number;
          serving_team_id: string; server_id: string;
          point_won_by_id: string; lost_reason: string | null;
          point_winner_id: string | null; recorded_at: string;
        }) => ({
          id: r.id,
          setId: r.set_id,
          rallyNumber: r.rally_number,
          servingTeamId: r.serving_team_id,
          serverId: r.server_id,
          pointWonById: r.point_won_by_id,
          lostReason: r.lost_reason as LostReason,
          pointWinnerId: r.point_winner_id,
          recordedAt: r.recorded_at,
        }));

        const formattedSet: SetRecord = {
          id: s.id,
          matchId: s.match_id,
          setNumber: s.set_number,
          scoreA: s.score_a,
          scoreB: s.score_b,
          winnerId: s.winner_id,
          startedAt: s.started_at,
          endedAt: s.ended_at,
          rallies: rallies.sort((a, b) => b.rallyNumber - a.rallyNumber),
        };
        sets.push(formattedSet);

        if (!s.ended_at) {
          // This is the current (in-progress) set
          currentSetNumber = s.set_number;

          // Sort rallies ascending by rally_number so rallies[last] is most recent
          const sortedRalliesAsc = [...rallies].sort((a, b) => a.rallyNumber - b.rallyNumber);
          const lastRally = sortedRalliesAsc[sortedRalliesAsc.length - 1];

          // Determine serving team: prefer persisted columns from the match record,
          // fall back to deriving from last rally outcome or set context.
          let servingTeamId: string;
          let currentServerId: string;

          const persistedServingTeamId = (data as any).current_serving_team_id;
          const persistedServerId = (data as any).current_server_id;

          if (persistedServingTeamId) {
            // Use the persisted serving team, but cross-check against the last rally.
            // If the last rally was a service break and the persisted team is the one
            // that LOST the rally (i.e. updateMatchServingTeam never ran after recording
            // the rally), override with the correct next-serving team.
            if (
              lastRally &&
              lastRally.pointWonById !== lastRally.servingTeamId &&  // service break
              persistedServingTeamId === lastRally.servingTeamId     // persisted = loser
            ) {
              servingTeamId = lastRally.pointWonById;
              currentServerId = '';
            } else {
              servingTeamId = persistedServingTeamId;
              currentServerId = persistedServerId || '';
            }
          } else if (lastRally) {
            // Fallback: derive from last rally outcome
            const serviceBreak = lastRally.pointWonById !== lastRally.servingTeamId;
            if (serviceBreak) {
              servingTeamId = lastRally.pointWonById;
              currentServerId = '';
            } else {
              servingTeamId = lastRally.servingTeamId;
              currentServerId = lastRally.serverId;
            }
          } else {
            // No rallies in this set yet - serving team needs to be manually selected
            servingTeamId = ''; // Will trigger serving team selection
            currentServerId = ''; // Will be selected after team selection
          }

          currentSet = {
            scoreA: s.score_a,
            scoreB: s.score_b,
            rallies: rallies.sort((a, b) => b.rallyNumber - a.rallyNumber),
            servingTeamId,
            currentServerId,
            goldenPoint: isGoldenPointState(s.score_a, s.score_b),
          };
        }
      }

      // Determine status from DB and actual state
      const dbStatus = data.status as MatchState['status'];
      let status = dbStatus;

      // Derive correct status from actual match state
      // If there are rallies in any set, the match is definitely not in 'first-server' phase
      const hasRallies = data.sets.some((s: { rallies: unknown[] }) => s.rallies && s.rallies.length > 0);
      const hasStartedSet = sets.some(s => !s.endedAt || s.scoreA > 0 || s.scoreB > 0);

      if (dbStatus === 'first-server' && hasStartedSet) {
        // DB status is stale - match actually started
        status = currentSet ? 'live' : 'set-won';
        console.log('[Recovery] Correcting stale DB status from "first-server" to', status);
      } else if (dbStatus === 'first-server' && hasRallies) {
        // Defensive: If rallies exist, match is live
        status = 'live';
        console.log('[Recovery] Correcting stale DB status - rallies exist, setting to "live"');
      }

      // If DB says set-won but we have no active set, create the next set
      // so the scorer can resume into Set N+1 without getting stuck on "Loading..."
      if (status === 'set-won' && !currentSet) {
        currentSetNumber = sets.length;
        console.log('[recoverMatch] Set-won with no active set. Creating set', currentSetNumber + 1);

        // Determine the starting team for the new set by alternating from the
        // previous set's starter (NOT who won/lost).
        const lastSet = sets[sets.length - 1];
        const nextServingTeamId = getNextSetStarter(
          lastSet?.rallies,
          lastSet?.winnerId ?? null,
          data.team_a_id,
          data.team_b_id,
          data.toss_won_by_id,
          data.toss_choice,
        );

        // Create the next set row in the DB
        const nextSetNumber = currentSetNumber + 1;
        const newSetRecord = await createSet(data.id, nextSetNumber);
        const formattedNewSet: SetRecord = {
          id: newSetRecord.id,
          matchId: newSetRecord.match_id,
          setNumber: newSetRecord.set_number,
          scoreA: 0,
          scoreB: 0,
          winnerId: null,
          startedAt: newSetRecord.started_at,
          endedAt: null,
        };
        sets.push(formattedNewSet);
        currentSetNumber = nextSetNumber;

        currentSet = {
          scoreA: 0,
          scoreB: 0,
          rallies: [],
          servingTeamId: nextServingTeamId,
          currentServerId: '', // No server selected yet — scorer will pick
          goldenPoint: false,
        };

        console.log('[recoverMatch] Created next set', {
          setNumber: nextSetNumber,
          setId: newSetRecord.id,
          servingTeamId: nextServingTeamId,
        });
      }

      // Calculate setsWon from set records
      const setsWonA = sets.filter(s => s.winnerId === teamA.id).length;
      const setsWonB = sets.filter(s => s.winnerId === teamB.id).length;

      // Reconstruct rotation state from rally history for each team
      const buildRotation = (teamId: string, allSets: typeof data.sets): RotationState => {
        const serveHistory: string[] = [];
        const cycleServed: string[] = [];
        // Only consider the current set for rotation tracking
        const currentSetData = allSets.find((s: { ended_at: string | null }) => !s.ended_at);
        if (!currentSetData) return { serveHistory: [], cycleServed: [] };

        const teamRallies = currentSetData.rallies
          .filter((r: { serving_team_id: string }) => r.serving_team_id === teamId)
          .sort((a: { rally_number: number }, b: { rally_number: number }) => a.rally_number - b.rally_number);

        for (const r of teamRallies) {
          // Serve fouls don't count toward rotation
          if (r.lost_reason === 'FOUL') continue;
          serveHistory.push(r.server_id);
          if (!cycleServed.includes(r.server_id)) {
            cycleServed.push(r.server_id);
          }
        }

        // If the current server belongs to this team but isn't yet in the rally records
        // (e.g. app was killed right after server selection, before any rally was recorded),
        // include them so rotation validation is correct after recovery.
        const currentServingTeamId = data.current_serving_team_id;
        const currentServerId = data.current_server_id;
        if (
          currentServingTeamId === teamId &&
          currentServerId &&
          !serveHistory.includes(currentServerId)
        ) {
          serveHistory.push(currentServerId);
          if (!cycleServed.includes(currentServerId)) cycleServed.push(currentServerId);
        }

        return { serveHistory, cycleServed };
      };

      const teamAState: TeamState = {
        id: teamA.id,
        name: teamA.name,
        shortName: teamA.shortName,
        color: teamA.color,
        logoUrl: teamA.logoUrl,
        players: teamA.players,
        setsWon: setsWonA,
        rotationState: buildRotation(teamA.id, data.sets),
      };
      const teamBState: TeamState = {
        id: teamB.id,
        name: teamB.name,
        shortName: teamB.shortName,
        color: teamB.color,
        logoUrl: teamB.logoUrl,
        players: teamB.players,
        setsWon: setsWonB,
        rotationState: buildRotation(teamB.id, data.sets),
      };

      autoSaveEnabled.current = false;
      console.log('[recoverMatch] Recovery summary:', {
        matchId: targetId,
        matchStatus: data.status,
        completedSets: sets.filter(s => s.endedAt).length,
        currentSetNumber,
        currentSet: currentSet ? {
          servingTeamId: currentSet.servingTeamId,
          currentServerId: currentSet.currentServerId,
          rallyCount: currentSet.rallies.length,
        } : null,
        derivedStatus: status,
        setsCount: sets.length,
      });
      setMatchState({
        match: formattedMatch,
        teamA: teamAState,
        teamB: teamBState,
        currentSetNumber,
        currentSet,
        status,
        sets,
        bestCatcherIds: data.best_catcher_ids || [],
        tournamentRules,
      });

      // Rebuild server selection snapshot so "Undo Server Selection" works after a
      // page reload when the server was picked but no rallies have been recorded yet.
      if (currentSet?.currentServerId && currentSet.rallies.length === 0 && currentSet.servingTeamId) {
        serverSelectionSnapshots.current.set(currentSet.servingTeamId, {
          rallyCount: 0,
          previousRotationState: { serveHistory: [], cycleServed: [] },
        });
      }

      // Re-enable auto-save after state is set
      requestAnimationFrame(() => { autoSaveEnabled.current = true; });
      setRecoveryRef(null);

      // Load timeout data
      try {
        const [activeTimeoutData, countsData] = await Promise.all([
          getActiveTimeout(data.id),
          getTimeoutCounts(data.id, currentSetNumber),
        ]);
        if (activeTimeoutData) {
          setActiveTimeout({
            id: activeTimeoutData.id,
            teamId: activeTimeoutData.teamId,
            teamName: activeTimeoutData.teamName,
            setNumber: activeTimeoutData.setNumber,
            startTimestamp: activeTimeoutData.startTimestamp,
          });
        } else {
          setActiveTimeout(null);
        }
        setTimeoutCounts(countsData);
      } catch (err) {
        console.warn('[recoverMatch] Could not load timeout data:', err);
      }
    } catch (err) {
      console.error('[recoverMatch] Failed:', err);
      clearMatchRef();
      setRecoveryRef(null);
      throw err;
    } finally {
      setRecoveryLoading(false);
    }
  }, [recoveryRef, teams]);

  const discardMatch = useCallback(async () => {
    // If there's a recovery ref with a matchId, mark it as discarded in the DB
    if (recoveryRef?.matchId) {
      try {
        await discardMatchInDB(recoveryRef.matchId);
        console.log('[discardMatch] Match marked as discarded in DB:', recoveryRef.matchId);
      } catch (err) {
        console.error('[discardMatch] Failed to mark match as discarded:', err);
      }
    }
    clearMatchRef();
    setRecoveryRef(null);
    setMatchState({
      match: null,
      teamA: null,
      teamB: null,
      currentSetNumber: 0,
      currentSet: null,
      status: 'setup',
      sets: [],
      bestCatcherIds: [],
    });
    setActiveTimeout(null);
    setTimeoutCounts(new Map());
  }, [recoveryRef]);

  const handleExitToTournament = useCallback((tournamentId: string) => {
    // Persist match ref so refresh recovery works when user returns from tournament
    if (matchState.match) {
      saveMatchRef({
        matchId: matchState.match.id,
        teamAId: matchState.teamA?.id ?? '',
        teamBId: matchState.teamB?.id ?? '',
        status: matchState.status,
        tournamentId: matchState.match.tournamentId,
      });
    }
    navigate(`/tournament/${tournamentId}`);
  }, [matchState, navigate]);

  const startMatch = useCallback(async (matchData: {
    matchNumber: number;
    date: string;
    time: string;
    matchType: MatchType;
    teamAId: string;
    teamBId: string;
    tossWonById: string;
    tossChoice: TossChoice;
    tournamentId: string;
    matchId?: string;
  }) => {
    if (!matchData.tournamentId) {
      navigate('/tournaments');
      throw new Error('A tournament is required to create a match.');
    }
    try {
      const teamA = teams.find(t => t.id === matchData.teamAId);
      const teamB = teams.find(t => t.id === matchData.teamBId);

      if (!teamA || !teamB) {
        throw new Error('Teams not found');
      }

      // Fetch tournament rules if tournamentId provided
      let tournamentRules: TournamentRules | null = null;
      let winningPoint: number | null = null;
      let goldenPoint: number | null = null;

      if (matchData.tournamentId) {
        try {
          const tournament = await getTournament(matchData.tournamentId);
          tournamentRules = {
            leagueWinningPoint: tournament.leagueWinningPoint,
            leagueGoldenPoint: tournament.leagueGoldenPoint,
            playoffWinningPoint: tournament.playoffWinningPoint,
            playoffGoldenPoint: tournament.playoffGoldenPoint,
          };
          // Snapshot the rules for this match based on match type
          if (matchData.matchType === 'league') {
            winningPoint = tournamentRules.leagueWinningPoint;
            goldenPoint = tournamentRules.leagueGoldenPoint;
          } else {
            winningPoint = tournamentRules.playoffWinningPoint;
            goldenPoint = tournamentRules.playoffGoldenPoint;
          }
          console.log('[startMatch] Tournament rules fetched:', {
            tournamentId: matchData.tournamentId,
            matchType: matchData.matchType,
            winningPoint,
            goldenPoint,
          });
        } catch (err) {
          console.warn('[startMatch] Could not fetch tournament rules, using defaults');
        }
      }

      // If matchId is provided, we're starting an existing scheduled match.
      // Otherwise we create a new match (legacy path from MatchSetup form).
      const match = matchData.matchId
        ? await startScheduledMatch(matchData.matchId, matchData.tossWonById, matchData.tossChoice)
        : await createMatch({
            matchNumber: matchData.matchNumber,
            date: matchData.date,
            matchType: matchData.matchType,
            teamAId: matchData.teamAId,
            teamBId: matchData.teamBId,
            tossWonById: matchData.tossWonById,
            tossChoice: matchData.tossChoice,
            tournamentId: matchData.tournamentId,
            winningPoint,
            goldenPoint,
            matchTime: matchData.time,
          });

      const formattedMatch: Match = {
        id: match.id,
        matchNumber: match.match_number,
        date: match.date,
        matchType: match.match_type as MatchType,
        teamAId: match.team_a_id,
        teamBId: match.team_b_id,
        tossWonById: match.toss_won_by_id,
        tossChoice: match.toss_choice as TossChoice,
        status: 'first-server',
        winnerId: null,
        bestCatcherIds: [],
        tournamentId: match.tournament_id,
        matchTime: match.match_time,
        winningPoint: match.winning_point,
        goldenPoint: match.golden_point,
        createdAt: match.created_at,
        updatedAt: match.updated_at,
        isLocked: false,
      };

      console.log('[startMatch] Match started with rules:', {
        matchId: match.id,
        winningPoint: match.winning_point,
        goldenPoint: match.golden_point,
      });

      setMatchState({
        match: formattedMatch,
        teamA: {
          id: teamA.id,
          name: teamA.name,
          shortName: teamA.shortName,
          color: teamA.color,
          logoUrl: teamA.logoUrl,
          players: teamA.players,
          setsWon: 0,
          rotationState: { serveHistory: [], cycleServed: [] },
        },
        teamB: {
          id: teamB.id,
          name: teamB.name,
          shortName: teamB.shortName,
          color: teamB.color,
          logoUrl: teamB.logoUrl,
          players: teamB.players,
          setsWon: 0,
          rotationState: { serveHistory: [], cycleServed: [] },
        },
        currentSetNumber: 0,
        currentSet: null,
        status: 'first-server',
        sets: [],
        bestCatcherIds: [],
        tournamentRules,
      });
      capture('match_started', {
        match_id: match.id,
        match_type: matchData.matchType,
        tournament_id: matchData.tournamentId,
        team_a: teamA.name,
        team_b: teamB.name,
      });
    } catch (err) {
      setError('Failed to start match');
      console.error(err);
      throw err;
    }
  }, [teams]);

  const selectFirstServer = useCallback(async (firstServingTeamId: string, player: Player) => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB) return;
    if (selectingFirstServer.current) return;
    selectingFirstServer.current = true;

    const servingTeamState = firstServingTeamId === matchState.teamA.id ? matchState.teamA : matchState.teamB;

    const validationError = validateFirstServerTeam(player, firstServingTeamId, servingTeamState.name);
    if (validationError) {
      selectingFirstServer.current = false;
      throw new Error(validationError.message);
    }

    try {
      // Create first set
      const setRecord = await createSet(matchState.match.id, 1);

      const formattedSet: SetRecord = {
        id: setRecord.id,
        matchId: setRecord.match_id,
        setNumber: setRecord.set_number,
        scoreA: 0,
        scoreB: 0,
        winnerId: null,
        startedAt: setRecord.started_at,
        endedAt: null,
      };

      // Update match status to 'live' in database and record start time
      await Promise.all([
        updateMatchStatus(matchState.match.id, 'live'),
        updateMatchTimes(matchState.match.id, { startedAt: new Date().toISOString() }),
      ]);

      // Save snapshot for potential undo - first server has no previous rallies
      const previousRotationState = { serveHistory: [], cycleServed: [] };
      serverSelectionSnapshots.current.set(firstServingTeamId, {
        rallyCount: 0,
        previousRotationState,
      });

      setMatchState(prev => ({
        ...prev,
        currentSetNumber: 1,
        currentSet: {
          scoreA: 0,
          scoreB: 0,
          rallies: [],
          servingTeamId: firstServingTeamId,
          currentServerId: player.id,
          goldenPoint: false,
        },
        status: 'live',
        sets: [formattedSet],
        teamA: prev.teamA ? {
          ...prev.teamA,
          rotationState: firstServingTeamId === prev.teamA.id
            ? { serveHistory: [player.id], cycleServed: [player.id] }
            : { serveHistory: [], cycleServed: [] },
        } : null,
        teamB: prev.teamB ? {
          ...prev.teamB,
          rotationState: firstServingTeamId === prev.teamB.id
            ? { serveHistory: [player.id], cycleServed: [player.id] }
            : { serveHistory: [], cycleServed: [] },
        } : null,
      }));

      // Persist the first serving team and server for the Public Live Portal.
      try {
        await updateMatchServingTeam(matchState.match!.id, firstServingTeamId, player.id);
      } catch (err) {
        console.error('[handleFirstServerSelected] Failed to persist serving team:', err);
      }
    } catch (err) {
      setError('Failed to start set');
      console.error(err);
      throw err;
    } finally {
      selectingFirstServer.current = false;
    }
  }, [matchState]);

  const recordRally = useCallback(async (
    winningTeamId: string,
    lostReason: LostReason | null,
    pointWinnerId: string | null
  ) => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB || !matchState.currentSet) return;

    if (rallyInProgress.current) return;
    rallyInProgress.current = true;

    // Check if match is locked
    if (matchState.match.isLocked) {
      rallyInProgress.current = false;
      throw new Error('This match is locked and cannot be modified.');
    }

    const currentSetDb = matchState.sets.find(s => s.endedAt === null) ?? matchState.sets[matchState.sets.length - 1];
    if (!currentSetDb) { rallyInProgress.current = false; return; }

    // Data integrity: a server must be assigned and must belong to the serving team.
    const servingTeam = matchState.currentSet.servingTeamId === matchState.teamA.id ? matchState.teamA : matchState.teamB;
    const serverId = matchState.currentSet.currentServerId;
    if (serverId && servingTeam) {
      const serverPlayer = servingTeam.players.find(p => p.id === serverId);
      if (!serverPlayer) {
        rallyInProgress.current = false;
        throw new Error('Server validation failed: the assigned server does not belong to the serving team.');
      }
    }

    // Validate point winner required
    if (lostReason === 'IN' || lostReason === 'TOUCH') {
      const validationError = validatePointWinnerRequired(lostReason, pointWinnerId);
      if (validationError) {
        rallyInProgress.current = false;
        throw new Error(validationError.message);
      }
    }

    try {
      const newRallyNumber = matchState.currentSet.rallies.length + 1;
      const currentServingTeamId = matchState.currentSet.servingTeamId;
      const currentServerId = matchState.currentSet.currentServerId;

      // Record rally in DB
      await createRally({
        setId: currentSetDb.id,
        rallyNumber: newRallyNumber,
        servingTeamId: currentServingTeamId,
        serverId: currentServerId,
        pointWonById: winningTeamId,
        lostReason,
        pointWinnerId,
      });

      // Track every rally recorded
      capture('rally_recorded', {
        match_id: matchState.match.id,
        set_number: matchState.currentSetNumber,
        lost_reason: lostReason,
        winning_team_id: winningTeamId,
      });

      // Update scores
      const isTeamA = winningTeamId === matchState.teamA.id;
      const newScoreA = isTeamA ? matchState.currentSet.scoreA + 1 : matchState.currentSet.scoreA;
      const newScoreB = isTeamA ? matchState.currentSet.scoreB : matchState.currentSet.scoreB + 1;

      // Check for set win
      const matchRules = getMatchRulesConfig();

      // Debug logging for scoring rules
      console.log('[recordRally] Set win check:', {
        matchType: matchState.match.matchType,
        winningPoint: matchRules?.winningPoint,
        goldenPoint: matchRules?.goldenPoint,
        currentScore: `${matchState.currentSet.scoreA}-${matchState.currentSet.scoreB}`,
        newScore: `${newScoreA}-${newScoreB}`,
      });

      const setWinResult = checkSetWin(newScoreA, newScoreB, matchRules);

      console.log('[recordRally] Set win result:', {
        winner: setWinResult.winner,
        goldenPoint: setWinResult.goldenPoint,
        decision: setWinResult.winner ? 'SET COMPLETE' : 'CONTINUE',
      });

      // Create rally record
      const rally: Rally = {
        id: uuidv4(),
        setId: currentSetDb.id,
        rallyNumber: newRallyNumber,
        servingTeamId: currentServingTeamId,
        serverId: currentServerId,
        pointWonById: winningTeamId,
        lostReason,
        pointWinnerId,
        recordedAt: new Date().toISOString(),
      };

      if (setWinResult.winner) {
        // Set won
        const winningTeam = setWinResult.winner === 'A' ? matchState.teamA : matchState.teamB;
        const setsWon = setWinResult.winner === 'A' ? matchState.teamA.setsWon + 1 : matchState.teamA.setsWon;
        const setsWonB = setWinResult.winner === 'B' ? matchState.teamB.setsWon + 1 : matchState.teamB.setsWon;

        await updateSet(currentSetDb.id, {
          scoreA: newScoreA,
          scoreB: newScoreB,
          winnerId: winningTeam.id,
          endedAt: new Date().toISOString(),
        });

        // Check for match win
        const matchWinner = checkMatchWin(setsWon, setsWonB, matchState.match.matchType);

        if (matchWinner) {
          await Promise.all([
            updateMatchStatus(matchState.match.id, 'match-won', matchWinner === 'A' ? matchState.teamA.id : matchState.teamB.id),
            updateMatchTimes(matchState.match.id, { endedAt: new Date().toISOString() }),
            updateMatchServingTeam(matchState.match.id, null, null),
          ]);

          setMatchState(prev => ({
            ...prev,
            teamA: prev.teamA ? { ...prev.teamA, setsWon } : null,
            teamB: prev.teamB ? { ...prev.teamB, setsWon: setsWonB } : null,
            currentSet: {
              ...prev.currentSet!,
              scoreA: newScoreA,
              scoreB: newScoreB,
              rallies: [rally, ...prev.currentSet!.rallies],
            },
            sets: prev.sets.map(s => s.id === currentSetDb.id ? {
              ...s,
              scoreA: newScoreA,
              scoreB: newScoreB,
              winnerId: winningTeam.id,
              endedAt: new Date().toISOString(),
            } : s),
            status: 'match-won',
            match: prev.match ? { ...prev.match, status: 'match-won', winnerId: matchWinner === 'A' ? prev.teamA!.id : prev.teamB!.id } : null,
          }));
          clearMatchRef();
          capture('match_won', {
            match_id: matchState.match.id,
            winner_id: matchWinner === 'A' ? matchState.teamA.id : matchState.teamB.id,
            winner_name: matchWinner === 'A' ? matchState.teamA.name : matchState.teamB.name,
            sets_won_a: setsWon,
            sets_won_b: setsWonB,
            tournament_id: matchState.match.tournamentId,
          });
        } else {
          // Set won but match continues
          await updateMatchStatus(matchState.match.id, 'set-won');

          // Clear rotation state for both teams - new set starts fresh
          // Also clear server selection snapshots to prevent undo across sets
          serverSelectionSnapshots.current.clear();

          setMatchState(prev => ({
            ...prev,
            teamA: prev.teamA ? { ...prev.teamA, setsWon, rotationState: { serveHistory: [], cycleServed: [] } } : null,
            teamB: prev.teamB ? { ...prev.teamB, setsWon: setsWonB, rotationState: { serveHistory: [], cycleServed: [] } } : null,
            currentSet: {
              ...prev.currentSet!,
              scoreA: newScoreA,
              scoreB: newScoreB,
              rallies: [rally, ...prev.currentSet!.rallies],
            },
            sets: prev.sets.map(s => s.id === currentSetDb.id ? {
              ...s,
              scoreA: newScoreA,
              scoreB: newScoreB,
              winnerId: winningTeam.id,
              endedAt: new Date().toISOString(),
            } : s),
            status: 'set-won',
          }));
          capture('set_won', {
            match_id: matchState.match.id,
            set_number: matchState.currentSetNumber,
            winner_id: winningTeam.id,
            winner_name: winningTeam.name,
            score_a: newScoreA,
            score_b: newScoreB,
          });
        }
      } else {
        // Set continues - persist score to DB
        await updateSet(currentSetDb.id, { scoreA: newScoreA, scoreB: newScoreB });

        // Check for service break
        let nextServingTeamId = currentServingTeamId;
        let nextServerId = currentServerId;
        let serviceBreak = false;

        if (winningTeamId !== currentServingTeamId) {
          // Service break: the winner becomes the new serving team.
          // Clear the server — the scorer will pick the new server via the
          // ServerSelectionModal, and selectNextServer will persist the
          // updated serving team + server to the DB at that point.
          nextServingTeamId = winningTeamId;
          nextServerId = '';
          serviceBreak = true;
        }

        setMatchState(prev => {
          const newTeamA = prev.teamA ? {
            ...prev.teamA,
            setsWon: setWinResult.winner === 'A' ? prev.teamA.setsWon + 1 : prev.teamA.setsWon,
          } : null;
          const newTeamB = prev.teamB ? {
            ...prev.teamB,
            setsWon: setWinResult.winner === 'B' ? prev.teamB.setsWon + 1 : prev.teamB.setsWon,
          } : null;

          return {
            ...prev,
            teamA: newTeamA,
            teamB: newTeamB,
            currentSet: {
              ...prev.currentSet!,
              scoreA: newScoreA,
              scoreB: newScoreB,
              goldenPoint: setWinResult.goldenPoint,
              servingTeamId: nextServingTeamId,
              currentServerId: nextServerId,
              rallies: [rally, ...prev.currentSet!.rallies],
            },
            status: serviceBreak ? 'live' : prev.status,
          };
        });
      }
    } catch (err) {
      setError('Failed to record rally');
      console.error(err);
      throw err;
    } finally {
      rallyInProgress.current = false;
    }
  }, [matchState]);

  const selectNextServer = useCallback(async (player: Player) => {
    if (!matchState.teamA || !matchState.teamB || !matchState.currentSet) return;

    const servingTeam = matchState.currentSet.servingTeamId === matchState.teamA.id ? matchState.teamA : matchState.teamB;

    // Data integrity: the selected server must belong to the serving team.
    if (player.teamId !== servingTeam.id) {
      throw new Error(`Invalid server: ${player.name} does not belong to ${servingTeam.name}.`);
    }

    // Save snapshot before selection for potential undo
    const previousRotationState = JSON.parse(JSON.stringify(servingTeam.rotationState));
    const currentRallyCount = matchState.currentSet.rallies.length;
    serverSelectionSnapshots.current.set(servingTeam.id, {
      rallyCount: currentRallyCount,
      previousRotationState,
    });

    // Validate rotation using the current closure state (pre-flight check).
    // The actual state update below re-derives from `prev` to avoid stale-closure races.
    const isDeadlock = checkRotationDeadlock(servingTeam);
    if (!isDeadlock) {
      const validationError = validateRotationGap(player, servingTeam, servingTeam.name);
      if (validationError) {
        throw new Error(validationError.message);
      }
    }

    // If this is the first server selection for a newly-created set during
    // recovery (status === 'set-won'), transition the match to live so the
    // scorer can continue recording rallies instead of being stuck on the
    // set-won banner.
    const shouldTransitionToLive = matchState.status === 'set-won';
    if (shouldTransitionToLive) {
      await updateMatchStatus(matchState.match!.id, 'live');
    }

    // Compute updated rotation state immutably — never mutate the existing object.
    // Done inside setMatchState updater so it always operates on the latest `prev`,
    // not the stale closure snapshot captured at call time.
    setMatchState(prev => {
      const isTeamAServing = prev.currentSet?.servingTeamId === prev.teamA?.id;
      const currentServingTeam = isTeamAServing ? prev.teamA! : prev.teamB!;

      // Re-derive deadlock from latest prev to avoid stale-closure races
      const prevIsDeadlock = checkRotationDeadlock(currentServingTeam);
      const baseHistory = prevIsDeadlock ? [] : [...currentServingTeam.rotationState.serveHistory];
      const baseCycle = prevIsDeadlock ? [] : [...currentServingTeam.rotationState.cycleServed];

      const newRotationState = {
        serveHistory: [...baseHistory, player.id],
        cycleServed: baseCycle.includes(player.id) ? baseCycle : [...baseCycle, player.id],
      };

      return {
        ...prev,
        currentSet: {
          ...prev.currentSet!,
          currentServerId: player.id,
        },
        teamA: isTeamAServing && prev.teamA ? {
          ...prev.teamA,
          rotationState: newRotationState,
        } : prev.teamA,
        teamB: !isTeamAServing && prev.teamB ? {
          ...prev.teamB,
          rotationState: newRotationState,
        } : prev.teamB,
        status: shouldTransitionToLive ? 'live' : prev.status,
      };
    });

    // Persist the updated serving team and server so the Public Live Portal
    // reflects the change immediately, before the next rally is recorded.
    // Fire-and-forget — do NOT await, so the UI closes the modal immediately
    // without waiting for the network round-trip.
    updateMatchServingTeam(matchState.match!.id, servingTeam.id, player.id).catch(err => {
      console.error('[selectNextServer] Failed to persist serving team:', err);
    });
  }, [matchState]);

  const recordServeFoul = useCallback(async (server: Player) => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB || !matchState.currentSet) return;

    const currentSetDb = matchState.sets.find(s => s.endedAt === null) ?? matchState.sets[matchState.sets.length - 1];
    if (!currentSetDb) return;

    const servingTeamId = matchState.currentSet.servingTeamId;
    const isServingTeamA = servingTeamId === matchState.teamA.id;
    const opponentId = isServingTeamA ? matchState.teamB.id : matchState.teamA.id;
    const newRallyNumber = matchState.currentSet.rallies.length + 1;
    // Rotation state is intentionally NOT updated — a serve foul is an invalid serve
    // and the player should not be counted in the cooldown/rotation cycle.

    try {
      await createRally({
        setId: currentSetDb.id,
        rallyNumber: newRallyNumber,
        servingTeamId,
        serverId: server.id,
        pointWonById: opponentId,
        lostReason: 'FOUL',
        pointWinnerId: null,
      });

      const newScoreA = isServingTeamA
        ? matchState.currentSet.scoreA
        : matchState.currentSet.scoreA + 1;
      const newScoreB = isServingTeamA
        ? matchState.currentSet.scoreB + 1
        : matchState.currentSet.scoreB;

      const matchRules = getMatchRulesConfig();
      console.log('[recordServeFoul] Set win check:', {
        matchType: matchState.match.matchType,
        winningPoint: matchRules?.winningPoint,
        goldenPoint: matchRules?.goldenPoint,
        newScore: `${newScoreA}-${newScoreB}`,
      });

      const setWinResult = checkSetWin(newScoreA, newScoreB, matchRules);

      const rally: Rally = {
        id: uuidv4(),
        setId: currentSetDb.id,
        rallyNumber: newRallyNumber,
        servingTeamId,
        serverId: server.id,
        pointWonById: opponentId,
        lostReason: 'FOUL',
        pointWinnerId: null,
        recordedAt: new Date().toISOString(),
      };

      if (setWinResult.winner) {
        const winningTeam = setWinResult.winner === 'A' ? matchState.teamA : matchState.teamB;
        const setsWon = setWinResult.winner === 'A' ? matchState.teamA.setsWon + 1 : matchState.teamA.setsWon;
        const setsWonB = setWinResult.winner === 'B' ? matchState.teamB.setsWon + 1 : matchState.teamB.setsWon;

        await updateSet(currentSetDb.id, {
          scoreA: newScoreA,
          scoreB: newScoreB,
          winnerId: winningTeam.id,
          endedAt: new Date().toISOString(),
        });

        const matchWinner = checkMatchWin(setsWon, setsWonB, matchState.match.matchType);

        if (matchWinner) {
          await Promise.all([
            updateMatchStatus(matchState.match.id, 'match-won', matchWinner === 'A' ? matchState.teamA.id : matchState.teamB.id),
            updateMatchTimes(matchState.match.id, { endedAt: new Date().toISOString() }),
            updateMatchServingTeam(matchState.match.id, null, null),
          ]);
          setMatchState(prev => ({
            ...prev,
            teamA: prev.teamA ? { ...prev.teamA, setsWon } : null,
            teamB: prev.teamB ? { ...prev.teamB, setsWon: setsWonB } : null,
            currentSet: {
              ...prev.currentSet!,
              scoreA: newScoreA,
              scoreB: newScoreB,
              rallies: [rally, ...prev.currentSet!.rallies],
            },
            sets: prev.sets.map(s => s.id === currentSetDb.id ? {
              ...s, scoreA: newScoreA, scoreB: newScoreB, winnerId: winningTeam.id, endedAt: new Date().toISOString(),
            } : s),
            status: 'match-won',
            match: prev.match ? {
              ...prev.match,
              status: 'match-won',
              winnerId: matchWinner === 'A' ? prev.teamA!.id : prev.teamB!.id,
            } : null,
          }));
          clearMatchRef();
        } else {
          await updateMatchStatus(matchState.match.id, 'set-won');
          // Clear rotation state for both teams - new set starts fresh
          // Also clear server selection snapshots to prevent undo across sets
          serverSelectionSnapshots.current.clear();

          setMatchState(prev => ({
            ...prev,
            teamA: prev.teamA ? { ...prev.teamA, setsWon, rotationState: { serveHistory: [], cycleServed: [] } } : null,
            teamB: prev.teamB ? { ...prev.teamB, setsWon: setsWonB, rotationState: { serveHistory: [], cycleServed: [] } } : null,
            currentSet: {
              ...prev.currentSet!,
              scoreA: newScoreA,
              scoreB: newScoreB,
              rallies: [rally, ...prev.currentSet!.rallies],
            },
            sets: prev.sets.map(s => s.id === currentSetDb.id ? {
              ...s, scoreA: newScoreA, scoreB: newScoreB, winnerId: winningTeam.id, endedAt: new Date().toISOString(),
            } : s),
            status: 'set-won',
          }));
        }
      } else {
        // Serve foul always breaks service to the opponent.
        // Persist score to DB before updating local state
        await updateSet(currentSetDb.id, { scoreA: newScoreA, scoreB: newScoreB });

        // Do NOT persist the serving team here — the scorer will pick the new
        // server via the ServerSelectionModal, and selectNextServer will persist
        // the updated serving team + server to the DB at that point.

        // teamA/teamB rotation states are left unchanged — the invalid serve does not count.
        setMatchState(prev => ({
          ...prev,
          currentSet: {
            ...prev.currentSet!,
            scoreA: newScoreA,
            scoreB: newScoreB,
            goldenPoint: setWinResult.goldenPoint,
            servingTeamId: opponentId,
            currentServerId: '',
            rallies: [rally, ...prev.currentSet!.rallies],
          },
        }));
      }
    } catch (err) {
      setError('Failed to record serve foul');
      console.error(err);
      throw err;
    }
  }, [matchState]);

  const startNewSet = useCallback(async (firstServingTeamId: string, firstServerId: string) => {
    if (!matchState.match || !matchState.teamA || !matchState.teamB) return;

    try {
      const newSetNumber = matchState.currentSetNumber + 1;

      // Reuse an existing pending set if one was already created (e.g. during match recovery).
      // Otherwise, create a new set row in the DB.
      const existingPendingSet = matchState.sets.find(
        s => s.endedAt === null && s.winnerId === null && s.setNumber === newSetNumber
      );

      let formattedSet: SetRecord;
      if (existingPendingSet) {
        formattedSet = existingPendingSet;
      } else {
        const setRecord = await createSet(matchState.match.id, newSetNumber);
        formattedSet = {
          id: setRecord.id,
          matchId: setRecord.match_id,
          setNumber: setRecord.set_number,
          scoreA: 0,
          scoreB: 0,
          winnerId: null,
          startedAt: setRecord.started_at,
          endedAt: null,
        };
      }

      await updateMatchStatus(matchState.match.id, 'live');

      // Use the manually selected serving team (no automatic alternation)
      const newSetStarter = firstServingTeamId;

      // Save snapshot for potential undo - rotation resets for new set
      const previousRotationState = { serveHistory: [], cycleServed: [] };
      serverSelectionSnapshots.current.set(newSetStarter, {
        rallyCount: 0,
        previousRotationState,
      });

      // Persist the new set's serving team and server for the Public Live Portal.
      // Fire-and-forget — do NOT await, so the UI transitions immediately.
      updateMatchServingTeam(matchState.match.id, newSetStarter, firstServerId).catch(err => {
        console.error('[startNewSet] Failed to persist serving team:', err);
      });

      setMatchState(prev => ({
        ...prev,
        currentSetNumber: newSetNumber,
        currentSet: {
          scoreA: 0,
          scoreB: 0,
          rallies: [],
          servingTeamId: newSetStarter,
          currentServerId: firstServerId,
          goldenPoint: false,
        },
        sets: [...prev.sets, formattedSet],
        status: 'live',
        teamA: prev.teamA ? {
          ...prev.teamA,
          rotationState: newSetStarter === prev.teamA.id
            ? { serveHistory: [firstServerId], cycleServed: [firstServerId] }
            : { serveHistory: [], cycleServed: [] },
        } : null,
        teamB: prev.teamB ? {
          ...prev.teamB,
          rotationState: newSetStarter === prev.teamB.id
            ? { serveHistory: [firstServerId], cycleServed: [firstServerId] }
            : { serveHistory: [], cycleServed: [] },
        } : null,
      }));

      // Reset timeout counts for the new set
      setTimeoutCounts(new Map());
    } catch (err) {
      setError('Failed to start new set');
      console.error(err);
      throw err;
    }
  }, [matchState]);

  const undoLastRally = useCallback(async () => {
    if (undoInProgress.current) return;
    if (!matchState.currentSet || !matchState.sets[matchState.sets.length - 1]) return;

    // Check if match is locked
    if (matchState.match?.isLocked) {
      throw new Error('This match is locked and cannot be modified.');
    }

    const lastRally = matchState.currentSet.rallies[0];
    if (!lastRally) return;

    if (!matchState.match || !matchState.teamA || !matchState.teamB) return;
    undoInProgress.current = true;

    const currentSetDb = matchState.sets.find(s => s.endedAt === null) ?? matchState.sets[matchState.sets.length - 1];
    const currentSetHadWinner = currentSetDb.winnerId !== null;

    try {
      await deleteRally(currentSetDb.id, lastRally.rallyNumber);

      // Restore serving state from the rally being undone
      const servingTeamId = lastRally.servingTeamId;
      const currentServerId = lastRally.serverId;

      // Compute scores from remaining rallies (for DB writes below).
      // Uses matchState closure here — acceptable since the rally was just deleted
      // from DB and we only need the count for updateSet. The authoritative
      // in-state rebuild happens inside setMatchState(prev=>) below.
      const remainingForDB = matchState.currentSet.rallies.slice(1);
      let scoreA = 0;
      let scoreB = 0;
      for (const rally of remainingForDB) {
        if (rally.pointWonById === matchState.teamA.id) scoreA++;
        else if (rally.pointWonById === matchState.teamB.id) scoreB++;
      }

      // Handle set winner reversal DB writes
      if (currentSetHadWinner) {
        const setWinResult = checkSetWin(scoreA, scoreB, getMatchRulesConfig());
        if (!setWinResult.winner) {
          await updateSet(currentSetDb.id, { scoreA, scoreB, winnerId: null, endedAt: undefined });
          await updateMatchStatus(matchState.match.id, 'live');
        } else {
          await updateSet(currentSetDb.id, { scoreA, scoreB });
        }
      } else {
        await updateSet(currentSetDb.id, { scoreA, scoreB });
      }

      const matchRules = getMatchRulesConfig();
      const goldenPoint = matchRules?.goldenPoint ?? (matchState.match?.matchType === 'league' ? DEFAULT_TOURNAMENT_RULES.leagueGoldenPoint : DEFAULT_TOURNAMENT_RULES.playoffGoldenPoint);
      const goldenPointTrigger = goldenPoint - 1;

      // Persist the restored serving team and server for the Public Live Portal.
      try {
        await updateMatchServingTeam(matchState.match.id, servingTeamId, currentServerId);
      } catch (err) {
        console.error('[undoLastRally] Failed to persist serving team:', err);
      }

      setMatchState(prev => {
        if (!prev.teamA || !prev.teamB || !prev.currentSet) return prev;

        // Use prev.currentSet.rallies (guaranteed fresh) not the stale closure
        const remainingRallies = prev.currentSet.rallies.slice(1);

        let scoreA = 0;
        let scoreB = 0;
        for (const rally of remainingRallies) {
          if (rally.pointWonById === prev.teamA.id) scoreA++;
          else if (rally.pointWonById === prev.teamB.id) scoreB++;
        }

        // Rebuild rotation from remaining rallies — always ascending by rally number
        const rebuildRotation = (teamId: string): RotationState => {
          const serveHistory: string[] = [];
          const cycleServed: string[] = [];
          const teamRallies = remainingRallies
            .filter(r => r.servingTeamId === teamId && r.lostReason !== 'FOUL')
            .sort((a, b) => a.rallyNumber - b.rallyNumber);
          for (const rally of teamRallies) {
            serveHistory.push(rally.serverId);
            if (!cycleServed.includes(rally.serverId)) {
              cycleServed.push(rally.serverId);
            }
          }
          return { serveHistory, cycleServed };
        };

        let newSets = prev.sets;
        let newSetsWonA = prev.teamA.setsWon;
        let newSetsWonB = prev.teamB.setsWon;
        let newStatus: MatchState['status'] = 'live';

        if (currentSetHadWinner) {
          const setWinResult = checkSetWin(scoreA, scoreB, getMatchRulesConfig());
          if (!setWinResult.winner) {
            newSets = prev.sets.map(s => s.id === currentSetDb.id
              ? { ...s, scoreA, scoreB, winnerId: null, endedAt: null }
              : s);
            if (currentSetDb.winnerId === prev.teamA.id) newSetsWonA = Math.max(0, newSetsWonA - 1);
            else if (currentSetDb.winnerId === prev.teamB.id) newSetsWonB = Math.max(0, newSetsWonB - 1);
          } else {
            newSets = prev.sets.map(s => s.id === currentSetDb.id ? { ...s, scoreA, scoreB } : s);
            newStatus = 'set-won';
          }
        } else {
          newSets = prev.sets.map(s => s.id === currentSetDb.id ? { ...s, scoreA, scoreB } : s);
        }

        return {
          ...prev,
          currentSet: {
            ...prev.currentSet,
            scoreA,
            scoreB,
            rallies: remainingRallies,
            servingTeamId,
            currentServerId,
            goldenPoint: scoreA === goldenPointTrigger && scoreB === goldenPointTrigger,
          },
          sets: newSets,
          teamA: {
            ...prev.teamA,
            setsWon: newSetsWonA,
            rotationState: rebuildRotation(prev.teamA.id),
          },
          teamB: {
            ...prev.teamB,
            setsWon: newSetsWonB,
            rotationState: rebuildRotation(prev.teamB.id),
          },
          status: newStatus,
        };
      });
    } catch (err) {
      setError('Failed to undo rally');
      console.error(err);
    } finally {
      undoInProgress.current = false;
    }
  }, [matchState]);

  const resetMatch = useCallback(() => {
    setMatchState({
      match: null,
      teamA: null,
      teamB: null,
      currentSetNumber: 0,
      currentSet: null,
      status: 'setup',
      sets: [],
      bestCatcherIds: [],
    });
    clearMatchRef();
    setError(null);
    setActiveTimeout(null);
    setTimeoutCounts(new Map());
  }, []);

  const updateBestCatcher = useCallback(async (playerIds: string[]) => {
    if (!matchState.match) return;
    // Check if match is locked
    if (matchState.match.isLocked) {
      throw new Error('This match is locked and cannot be modified.');
    }
    try {
      await updateMatchBestCatcher(matchState.match.id, playerIds, matchState.match.tournamentId ?? undefined);
      setMatchState(prev => ({
        ...prev,
        bestCatcherIds: playerIds,
        match: prev.match ? { ...prev.match, bestCatcherIds: playerIds } : null,
      }));
    } catch (err) {
      console.error('Failed to update Best Catcher:', err);
      throw err;
    }
  }, [matchState.match]);

  const canUndoServerSelection = useCallback((): { canUndo: boolean; servingTeamId: string | null } => {
    if (!matchState.currentSet || !matchState.teamA || !matchState.teamB) {
      return { canUndo: false, servingTeamId: null };
    }

    const servingTeamId = matchState.currentSet.servingTeamId;
    const snapshot = serverSelectionSnapshots.current.get(servingTeamId);

    if (!snapshot) {
      return { canUndo: false, servingTeamId: null };
    }

    const currentRallyCount = matchState.currentSet.rallies.length;
    const canUndo = currentRallyCount === snapshot.rallyCount;

    return { canUndo, servingTeamId: canUndo ? servingTeamId : null };
  }, [matchState]);

  const undoServerSelection = useCallback(async () => {
    if (!matchState.teamA || !matchState.teamB || !matchState.currentSet) return;

    const { servingTeamId } = canUndoServerSelection();
    if (!servingTeamId) return;

    const snapshot = serverSelectionSnapshots.current.get(servingTeamId);
    if (!snapshot) return;

    const isTeamAServing = servingTeamId === matchState.teamA.id;

    setMatchState(prev => ({
      ...prev,
      currentSet: prev.currentSet ? {
        ...prev.currentSet,
        currentServerId: '',
      } : null,
      teamA: isTeamAServing && prev.teamA ? {
        ...prev.teamA,
        rotationState: snapshot.previousRotationState,
      } : prev.teamA,
      teamB: !isTeamAServing && prev.teamB ? {
        ...prev.teamB,
        rotationState: snapshot.previousRotationState,
      } : prev.teamB,
    }));

    // Persist the cleared server so the Public Live Portal reflects the undo.
    try {
      await updateMatchServingTeam(matchState.match!.id, servingTeamId, null);
    } catch (err) {
      console.error('[undoServerSelection] Failed to persist serving team:', err);
    }

    serverSelectionSnapshots.current.delete(servingTeamId);
  }, [matchState, canUndoServerSelection]);

  // Timeout handlers
  const handleStartTimeout = useCallback(async (teamId: string, teamName: string) => {
    if (!matchState.match || !matchState.currentSetNumber) return;

    const record = await startTimeout({
      matchId: matchState.match.id,
      setNumber: matchState.currentSetNumber,
      teamId,
      teamName,
    });

    setActiveTimeout({
      id: record.id,
      teamId: record.teamId,
      teamName: record.teamName,
      setNumber: record.setNumber,
      startTimestamp: record.startTimestamp,
    });

    // Refresh timeout counts so the scoreboard updates immediately
    const counts = await getTimeoutCounts(matchState.match.id, matchState.currentSetNumber);
    setTimeoutCounts(counts);
    capture('timeout_started', {
      match_id: matchState.match.id,
      set_number: matchState.currentSetNumber,
      team_id: teamId,
      team_name: teamName,
    });
  }, [matchState.match, matchState.currentSetNumber]);

  const handleEndTimeout = useCallback(async () => {
    if (!activeTimeout) return;

    console.log('[TIMEOUT DEBUG] handleEndTimeout activeTimeout:', {
      id: activeTimeout.id,
      teamId: activeTimeout.teamId,
      setNumber: activeTimeout.setNumber,
    });

    await endTimeout(activeTimeout.id, activeTimeout.startTimestamp);
    capture('timeout_ended', {
      match_id: matchState.match?.id,
      set_number: activeTimeout.setNumber,
      team_id: activeTimeout.teamId,
      team_name: activeTimeout.teamName,
    });
    setActiveTimeout(null);

    console.log('[TIMEOUT DEBUG] handleEndTimeout calling getTimeoutCounts:', {
      matchId: matchState.match?.id,
      setNumber: matchState.currentSetNumber,
    });

    // Refresh timeout counts for current set
    if (matchState.match && matchState.currentSetNumber) {
      const counts = await getTimeoutCounts(matchState.match.id, matchState.currentSetNumber);
      console.log('[TIMEOUT DEBUG] handleEndTimeout received counts:', Object.fromEntries(counts));
      setTimeoutCounts(counts);
    }
  }, [activeTimeout, matchState.match, matchState.currentSetNumber]);

  const servingTeamState = useMemo(() => {
    if (!matchState.currentSet || !matchState.teamA || !matchState.teamB) return null;
    return matchState.currentSet.servingTeamId === matchState.teamA.id ? matchState.teamA : matchState.teamB;
  }, [matchState]);

  const currentServer = useMemo(() => {
    if (!matchState.currentSet || !servingTeamState) return null;
    return servingTeamState.players.find(p => p.id === matchState.currentSet?.currentServerId) || null;
  }, [matchState.currentSet, servingTeamState]);

  const eligibleServersForServiceBreak = useMemo(() => {
    if (!servingTeamState) return { eligible: [], ineligible: [] };
    return getEligibleServers(servingTeamState);
  }, [servingTeamState]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-surface-raised border-t-team-a rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading teams...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-error text-lg mb-4">{error}</p>
          <button
            onClick={() => { setError(null); loadTeams(); }}
            className="px-6 py-3 bg-team-a text-text-primary rounded-lg font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Public routes accessible without authentication (live score portal)
  const publicLiveRoutes = (
    <>
      <Route path="/live" element={<Navigate to="/centre" replace />} />
      <Route path="/live/:broadcastId" element={<PublicLiveScoreboard />} />
      <Route path="/centre" element={<PublicTournamentCentre />} />
      <Route path="/centre/:tournamentId" element={<PublicTournamentCentre />} />
      <Route path="/player/:playerSlug" element={<PublicPlayerProfile />} />
      <Route path="/overlay/:broadcastId" element={<BroadcastOverlay />} />
    </>
  );

  // Check if we're on the overlay route - it needs transparent background
  const isOverlayRoute = location.pathname.startsWith('/overlay/');
  const isPlayerRoute = location.pathname.startsWith('/player/');

  // Login route is public; live portal routes are also public
  if (!user) {
    // Overlay route doesn't need the background wrapper
    if (isOverlayRoute) {
      return (
        <Routes>
          {publicLiveRoutes}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      );
    }
    return (
      <div className="min-h-screen bg-background">
        <DevBanner />
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          {publicLiveRoutes}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    );
  }

  if (isPlayerRoute) {
    return (
      <div className="min-h-screen bg-background">
        <DevBanner />
        <Routes>
          {publicLiveRoutes}
          <Route path="*" element={<Navigate to="/centre" replace />} />
        </Routes>
      </div>
    );
  }

  // Overlay route doesn't need the background wrapper
  if (isOverlayRoute) {
    return (
      <Routes>
        {publicLiveRoutes}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <DevBanner />
      <Routes>
        <Route
          path="/"
          element={
            isViewer ? <Navigate to="/scorer" replace /> :
            // If a different scheduled match is being started, show MatchSetup
            // regardless of current matchState — the active match is a different one.
            (matchState.status === 'setup' || (startScheduledMatchId && matchState.match?.id !== startScheduledMatchId)) ? (
              <MatchSetup
                teams={teams}
                onStartMatch={startMatch}
                recoveryRef={recoveryRef}
                onRecoverMatch={recoverMatch}
                onDiscardMatch={discardMatch}
                recoveryLoading={recoveryLoading}
                onSignOut={signOut}
                tournamentId={preselectedTournamentId ?? undefined}
                tournamentName={preselectedTournament?.name ?? undefined}
                scheduledMatchId={startScheduledMatchId ?? undefined}
                scheduledTeamAId={scheduledMatchData?.teamAId}
                scheduledTeamBId={scheduledMatchData?.teamBId}
                scheduledMatchNumber={scheduledMatchData?.matchNumber}
                scheduledMatchType={scheduledMatchData?.matchType}
                scheduledDate={scheduledMatchData?.date}
                scheduledTime={scheduledMatchData?.matchTime}
              />
            ) : matchState.status === 'first-server' ? (
              <FirstServerSelection
                matchState={matchState}
                teams={teams}
                onSelectServer={selectFirstServer}
                onSignOut={signOut}
              />
            ) : matchState.status === 'match-won' ? (
              <MatchComplete
                matchState={matchState}
                teams={teams}
                onNewMatch={resetMatch}
                onUpdateBestCatcher={updateBestCatcher}
                onSignOut={signOut}
              />
            ) : (
              <LiveScoring
                matchState={matchState}
                servingTeam={servingTeamState}
                currentServer={currentServer}
                eligibleServers={eligibleServersForServiceBreak}
                serverSelectionNeeded={!!matchState.currentSet && !matchState.currentSet.currentServerId}
                onRecordRally={recordRally}
                onSelectNextServer={selectNextServer}
                onUndo={undoLastRally}
                onUndoServerSelection={undoServerSelection}
                canUndoServerSelection={canUndoServerSelection}
                onStartNewSet={startNewSet}
                onRecordServeFoul={recordServeFoul}
                onFetchSetRallies={fetchSetRallies}
                onUpdateBestCatcher={updateBestCatcher}
                onSignOut={signOut}
                tournamentId={matchState.match?.tournamentId ?? undefined}
                onExitToTournament={matchState.match?.tournamentId ? handleExitToTournament : undefined}
                activeTimeout={activeTimeout}
                timeoutCounts={timeoutCounts}
                onStartTimeout={handleStartTimeout}
                onEndTimeout={handleEndTimeout}
              />
            )
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/teams"
          element={
            <ProtectedRoute adminOnly>
              <AdminPanel teams={teams} onDataChanged={reloadTeams} onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/players"
          element={
            <ProtectedRoute adminOnly>
              <AdminPanel teams={teams} onDataChanged={reloadTeams} onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/club"
          element={
            <ProtectedRoute adminOnly>
              <ClubSettings onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute adminOnly>
              <UserManagement onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tournaments"
          element={<TournamentManagement onSignOut={signOut} />}
        />
        <Route
          path="/tournament/:tournamentId"
          element={<TournamentDashboard onSignOut={signOut} />}
        />
        <Route
          path="/match/:matchId/resume"
          element={
            <ResumeMatchScreen
              matchState={matchState}
              servingTeam={servingTeamState}
              currentServer={currentServer}
              eligibleServers={eligibleServersForServiceBreak}
              teams={teams}
              recoveryLoading={recoveryLoading}
              recoverMatch={recoverMatch}
              resetMatch={resetMatch}
              updateBestCatcher={updateBestCatcher}
              recordRally={recordRally}
              selectNextServer={selectNextServer}
              undoLastRally={undoLastRally}
              undoServerSelection={undoServerSelection}
              canUndoServerSelection={canUndoServerSelection}
              startNewSet={startNewSet}
              recordServeFoul={recordServeFoul}
              fetchSetRallies={fetchSetRallies}
              selectFirstServer={selectFirstServer}
              signOut={signOut}
              onExitToTournament={handleExitToTournament}
              activeTimeout={activeTimeout}
              timeoutCounts={timeoutCounts}
              onStartTimeout={handleStartTimeout}
              onEndTimeout={handleEndTimeout}
            />
          }
        />
        <Route
          path="/match/:matchId/view"
          element={<MatchView onSignOut={signOut} />}
        />
        <Route
          path="/admin/matches"
          element={
            <ProtectedRoute adminOnly>
              <AdminMatchManagement onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/rally-editor"
          element={
            <ProtectedRoute adminOnly>
              <RallyEditor onSignOut={signOut} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/broadcast"
          element={
            <ProtectedRoute adminOnly>
              <BroadcastControl />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scorer"
          element={<ScorerDashboard onSignOut={signOut} />}
        />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/live" element={<Navigate to="/centre" replace />} />
        <Route path="/live/:broadcastId" element={<PublicLiveScoreboard />} />
        <Route path="/centre" element={<PublicTournamentCentre />} />
        <Route path="/centre/:tournamentId" element={<PublicTournamentCentre />} />
        <Route path="/overlay/:broadcastId" element={<BroadcastOverlay />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
